/**
 * This library provides a set of utilities for parsing and autocompleting Power Query commands.
 */

import uniq from 'lodash/uniq';
import cloneDeep from 'lodash/cloneDeep';

import { FacetsCompletionInfo } from 'common/types';

import {
  ANY_TYPE,
  FILTER_STATE,
  FilterState,
  Match,
  MatchInfo,
  PARSE_TYPE,
  Subquery,
  Token,
  TOKEN_TYPE,
} from './types';

import {
  AGGREGATE_TOKEN_TYPE,
  COMMANDS,
  COMPLETE_EMPTY_STRING,
  COMPLEX_COMMANDS,
  EMPTY_FILTER_STATE,
  FILTER_STATE_NEXT,
  PQ_FUNCTIONS,
  PQ_FUNCTIONS_NO_ARGS,
  PQ_IN_FUNCTION_STATE,
  WORDS,
} from './constants';

import { findIndexOfLastToken, isPowerQuery, tokenizeFilter } from './parse';

import { getFilterMatches, getFilterMatchesByType, getFilterState } from './filter';

// return true if token is an '='
export const isAssignment = (token: Token): boolean => token.type === TOKEN_TYPE.COMPARISON && token.text === '=';

// Returns true if the given token is a PQ function
export const isPowerQueryFunction = (type: ANY_TYPE): boolean =>
  type === TOKEN_TYPE.PQ_GROUP_FUNCTION ||
  AGGREGATE_TOKEN_TYPE[TOKEN_TYPE.PQ_EXPRESSION_FUNCTION].includes(type as TOKEN_TYPE);

/**
 * Tokenize a PQ the same way we do a filter, then go through and refine the types of some tokens. Specifically, we
 * recognize group and expression functions, and mark fields created by certain commands such as 'let' with the
 * type PQ_FIELD, so we can make those available for later matching.
 *
 * @param query    a power query
 * @return a list of typed tokens
 */
export const tokenizePowerQuery = (query: string): Token[] => {
  const filterTokens = tokenizeFilter(query);
  const tokens = [] as Token[];

  // first, look for PQ commands
  for (let i = 0; i < filterTokens.length; i += 1) {
    const token = filterTokens[i];
    const nextToken = filterTokens[i + 1] || ({} as Token);
    const nextNextToken = filterTokens[i + 2] || ({} as Token);
    tokens.push(token); // this is the pipe
    // look for two-word commands 'inner join' and 'left join'
    // 'in' to start 'inner join' can fool us since it's typed as COMPARISON
    if (token.type === TOKEN_TYPE.PIPE && (nextToken.type === TOKEN_TYPE.WORD || nextToken.text === 'in')) {
      nextToken.type = TOKEN_TYPE.COMMAND;
      // const func = token.text;
      if (nextToken.complete && ['inner', 'left'].includes(nextToken.text) && nextNextToken.type === TOKEN_TYPE.WORD) {
        nextToken.end = nextNextToken.end;
        nextToken.text = [nextToken.text, nextNextToken.text].join(' ');
        nextToken.complete = nextNextToken.complete;
        i += 2;
      } else {
        i += 1;
      }
      tokens.push(nextToken); // this is the (possibly two-word) command
    }
  }

  for (let i = 0; i < filterTokens.length; i += 1) {
    const token = filterTokens[i];
    const nextToken = filterTokens[i + 1] || ({} as Token);
    if (
      [TOKEN_TYPE.FUNCTION, TOKEN_TYPE.WORD].includes(token.type as TOKEN_TYPE) &&
      nextToken?.type === TOKEN_TYPE.LEFT_PAREN
    ) {
      const func = token.text;
      if (func === 'max' || func === 'min') {
        // these can be either numeric or grouping functions; look back for 'group' command
        token.type = TOKEN_TYPE.PQ_NUMERIC_FUNCTION;
        let j = i;
        while (j > 0 && tokens[j].type !== TOKEN_TYPE.COMMAND) {
          j -= 1;
        }
        const command = tokens[j].text;
        // function either follows the 'group' command, or is right side of setting a PQ field
        if (command === 'group' && (j === i - 1 || isAssignment(tokens[i - 1]))) {
          token.type = TOKEN_TYPE.PQ_GROUP_FUNCTION;
        }
      } else {
        token.type = Object.keys(PQ_FUNCTIONS).find((key) =>
          PQ_FUNCTIONS[key as TOKEN_TYPE].includes(func),
        ) as TOKEN_TYPE;
      }
    } else if (token.type === TOKEN_TYPE.WORD && token.text === 'by') {
      token.type = TOKEN_TYPE.BY;
    } else if (token.type === TOKEN_TYPE.WORD && token.text === 'from') {
      token.type = TOKEN_TYPE.FROM;
    } else if (token.type === TOKEN_TYPE.WORD && token.text === 'on') {
      token.type = TOKEN_TYPE.ON;
    }
  }

  return tokens;
};

/**
 * Look through the tokens for any that are fields being created by the power query, and set their type to PQ_FIELD.
 * The 'parse' command can create new tokens, which are returned.
 *
 * Note: this function is exported to help in unit tests, but it is not pure as it modifies the 'tokens' argument
 *
 * @param tokens    list of tokens
 * @param start     index of first token to consider
 * @param end       one past index of last token to consider
 *
 * @return list of new PQ_FIELD tokens found in 'parse' commands
 */
export const findPowerQueryFields = (tokens: Token[], start = 0, end = tokens.length - 1): Token[] => {
  // find next COMMAND token past the given start
  const getNextCommand = (tokenList: Token[], from: number): number =>
    tokenList.findIndex((token, index) => index > from && index <= end && token.type === TOKEN_TYPE.COMMAND);

  // find the token that indicates a command has ended
  const getCommandEnd = (tokenList: Token[], from: number): number =>
    tokenList.findIndex(
      (token, index) =>
        index > from &&
        index <= end &&
        [token.type === TOKEN_TYPE.PIPE, TOKEN_TYPE.BY, TOKEN_TYPE.FROM].includes(token.type as TOKEN_TYPE),
    );

  // Find fields created by the PQ. To do that, we need to go command by command and look for fields based on specific command syntax.
  let cmdIndex = getNextCommand(tokens, start);
  let cmdToken = tokens[cmdIndex];
  const pqFields: Token[] = []; // append fields created by 'parse' since they are created within a string and are not tokens

  while (cmdToken) {
    const command = cmdToken.text;
    const nextToken = tokens[cmdIndex + 1];
    if (command === 'let' && nextToken?.complete) {
      // 'let' always takes form: let FIELD = EXPR
      nextToken.type = TOKEN_TYPE.PQ_FIELD;
    } else if (command === 'parse' && nextToken?.complete && nextToken?.text) {
      // 'parse' turns facets into PQ fields by surrounding them with $; create a PQ_FIELD token for each one we find
      // use 'exec' because String method 'matchAll' not available in our CI environment
      const regex = /\$(\w+)\$/g;
      let m = regex.exec(nextToken.text);
      while (m !== null) {
        pqFields.push({ parseType: PARSE_TYPE.TOKEN, type: TOKEN_TYPE.PQ_FIELD, text: m[1] } as Token);
        m = regex.exec(nextToken.text);
      }
    } else if (command === 'columns' || command === 'group' || command === 'lookup') {
      let endIndex = getCommandEnd(tokens, cmdIndex);
      if (endIndex === -1) {
        endIndex = end;
      }
      // for these commands, look through the end of the command for the sequence: FIELD =
      for (let i = cmdIndex + 1; i < endIndex; i += 1) {
        const token = tokens[i];
        const next = tokens[i + 1];
        if (token.type === TOKEN_TYPE.FIELD && isAssignment(next)) {
          token.type = TOKEN_TYPE.PQ_FIELD;
        }
      }
    }
    cmdIndex = getNextCommand(tokens, cmdIndex);
    cmdToken = tokens[cmdIndex];
  }

  return pqFields;
};

/**
 * Return the state for the given expression. Mostly it just recognizes the start of an expression and the body
 * of a PQ function.
 *
 * @param tokens    a list of tokens representing a (possibly incomplete) expression
 * @return filter state
 */
const getExpressionState = (tokens: Token[]): FilterState => {
  const states = [] as FILTER_STATE[];
  const last = tokens[tokens.length - 1] || ({} as Token);
  const lastType = last?.type;
  const text = (!last?.complete && last.text) || '';
  let field = '';

  // find function we're currently in so we can set context as numeric or string if appropriate
  const lastFuncTokenIndex = findIndexOfLastToken(tokens, (token) => isPowerQueryFunction(token?.type));
  const curFuncToken = tokens[lastFuncTokenIndex];

  if (tokens.length === 1 && lastType === TOKEN_TYPE.WORD && !last.complete) {
    // got a bit of text to start an expression, eg: '|let foo = t'
    states.push(FILTER_STATE.IN_PQ_EXPRESSION_START);
  } else if (lastType === TOKEN_TYPE.WORD && curFuncToken) {
    // got a bit of text as argument to a function, eg: '|let foo = floor(t' or '|let foo = len(userName) + len(ho'
    states.push(PQ_IN_FUNCTION_STATE[curFuncToken.type as TOKEN_TYPE] as FILTER_STATE);
    field = text;
  }

  const queryState = cloneDeep(EMPTY_FILTER_STATE) as FilterState;
  queryState.states = states;
  queryState.field = field;
  queryState.text = text;
  queryState.isNumeric = curFuncToken?.type === TOKEN_TYPE.PQ_NUMERIC_FUNCTION;
  queryState.isString = curFuncToken?.type === TOKEN_TYPE.PQ_STRING_FUNCTION;

  return queryState;
};

/**
 * Return the state for a 'filter' command.
 *
 * @param tokens    list of tokens representing the body of the command
 * @param query     text of the PQ
 *
 * @return filter state
 */
const getFilterCommandState = (tokens: Token[], query: string): FilterState => {
  const filter = tokens.length > 0 ? query.substring(tokens[0].start) : '';
  const filterState = getFilterState(filter);
  if (filterState.valid.includes(TOKEN_TYPE.FIELD)) {
    filterState.states.push(FILTER_STATE.IN_PQ_COLUMN);
  }

  return filterState;
};

/**
 * Return the state for a 'let' command.
 *
 * @param tokens    list of tokens representing the body of the command
 * @return filter state
 */
const getLetCommandState = (tokens: Token[]): FilterState => {
  const last = tokens[tokens.length - 1] || ({} as Token);

  if (isAssignment(last)) {
    const queryState = cloneDeep(EMPTY_FILTER_STATE) as FilterState;
    queryState.states.push(FILTER_STATE.PQ_EXPRESSION_START);
    queryState.text = '=';
    queryState.op = '=';

    return queryState;
  }

  return getExpressionState(tokens.slice(2));
};

/**
 * Return the state for a 'columns' command.
 *
 * @param tokens    list of tokens representing the body of the command
 * @return filter state
 */
const getColumnsCommandState = (tokens: Token[]): FilterState => {
  const last = tokens[tokens.length - 1] || ({} as Token);
  const one = tokens[tokens.length - 2] || ({} as Token); // one token back

  if (
    tokens.length === 1 ||
    isAssignment(last) ||
    (last?.type === TOKEN_TYPE.WORD && (isAssignment(one) || one?.type === TOKEN_TYPE.COMMA))
  ) {
    const queryState = cloneDeep(EMPTY_FILTER_STATE) as FilterState;
    queryState.states.push(FILTER_STATE.PQ_EXPRESSION_START);
    queryState.text = last.text;

    return queryState;
  }

  const lastSeparatorTokenIndex = findIndexOfLastToken(
    tokens,
    (token) => isAssignment(token) || token?.type === TOKEN_TYPE.COMMA,
  );
  const expTokens = lastSeparatorTokenIndex === -1 ? tokens : tokens.slice(lastSeparatorTokenIndex + 1);

  return getExpressionState(expTokens);
};

/**
 * Return the state for a 'group' command.
 *
 * @param tokens    list of tokens representing the body of the command
 * @return filter state
 */
const getGroupCommandState = (tokens: Token[]): FilterState => {
  const last = tokens[tokens.length - 1] || ({} as Token);
  const one = tokens[tokens.length - 2] || ({} as Token); // one token back
  const states = [];

  const byFound = !!tokens.find((token) => token.type === TOKEN_TYPE.BY);

  if (
    tokens.length === 1 ||
    isAssignment(last) ||
    (last?.type === TOKEN_TYPE.WORD && (isAssignment(one) || one?.type === TOKEN_TYPE.COMMA) && !byFound)
  ) {
    // we're expecting an expression; add PQ_GROUP state to pull in group functions
    states.push(FILTER_STATE.PQ_EXPRESSION_START, FILTER_STATE.PQ_GROUP);
  } else if (
    (last.type === TOKEN_TYPE.WORD && one.type === TOKEN_TYPE.BY) ||
    (one?.type === TOKEN_TYPE.COMMA && byFound)
  ) {
    // the 'by' keyword takes one or more comma-separated fields
    states.push(FILTER_STATE.IN_PQ_COLUMN);
  }

  if (states.length > 0) {
    const queryState = cloneDeep(EMPTY_FILTER_STATE) as FilterState;
    queryState.states = states;
    queryState.text = last.text;

    return queryState;
  }

  const lastSeparatorTokenIndex = findIndexOfLastToken(
    tokens,
    (token) => isAssignment(token) || token?.type === TOKEN_TYPE.COMMA,
  );
  const expTokens = lastSeparatorTokenIndex === -1 ? tokens : tokens.slice(lastSeparatorTokenIndex + 1);
  const queryState = getExpressionState(expTokens);
  queryState.states.push(FILTER_STATE.PQ_GROUP);

  return queryState;
};

/**
 * Return the state for a 'lookup' command. It introduces a new type of token: the name of a column in a data table (which is a
 * specially formatted config file). We don't need a TOKEN_TYPE for table column because we don't do anything with it. If we were
 * to get table information beforehand and want to match against table columns, we would add the token type.
 *
 * @param tokens    list of tokens representing the body of the command
 * @return filter state
 */
const getLookupCommandState = (tokens: Token[]): FilterState => {
  const last = tokens[tokens.length - 1] || ({} as Token);
  const one = tokens[tokens.length - 2] || ({} as Token); // one token back
  const states = [];

  const fromFound = !!tokens.find((token) => token.type === TOKEN_TYPE.FROM);
  const byFound = !!tokens.find((token) => token.type === TOKEN_TYPE.BY);

  if (!fromFound && last.type === TOKEN_TYPE.WORD) {
    states.push(last.complete ? FILTER_STATE.PQ_TABLE_COLUMN : FILTER_STATE.IN_PQ_TABLE_COLUMN);
  } else if (fromFound && !byFound && (last.type === TOKEN_TYPE.STRING || one.type === TOKEN_TYPE.STRING)) {
    states.push(FILTER_STATE.PQ_TABLE);
  } else if (
    (last.type === TOKEN_TYPE.WORD && one.type === TOKEN_TYPE.BY) ||
    (one?.type === TOKEN_TYPE.COMMA && byFound)
  ) {
    // the 'by' keyword takes one or more comma-separated fields
    states.push(FILTER_STATE.IN_PQ_COLUMN);
  }

  const queryState = cloneDeep(EMPTY_FILTER_STATE) as FilterState;
  queryState.states = states;
  queryState.text = last.text;

  return queryState;
};

/**
 * Return the state for a 'parse' command.
 *
 * @param tokens    list of tokens representing the body of the command
 * @return filter state
 */
const getParseCommandState = (tokens: Token[]): FilterState => {
  const last = tokens[tokens.length - 1] || ({} as Token);
  const one = tokens[tokens.length - 2] || ({} as Token); // one token back

  if (last.type === TOKEN_TYPE.WORD && one.type === TOKEN_TYPE.FROM) {
    const queryState = cloneDeep(EMPTY_FILTER_STATE) as FilterState;
    queryState.states.push(FILTER_STATE.IN_PQ_COLUMN);
    queryState.text = last.text;

    return queryState;
  }

  return EMPTY_FILTER_STATE;
};

/**
 * Return state for a command that takes a single column argument. Works for 'sort' and 'transpose'.
 *
 * @param tokens    list of command body tokens
 * @return query state
 */
const getSimpleCommandState = (tokens: Token[]): FilterState => {
  const queryState = cloneDeep(EMPTY_FILTER_STATE) as FilterState;
  queryState.states.push(FILTER_STATE.IN_PQ_COLUMN);
  queryState.text = tokens[tokens.length - 1]?.text || '';

  return queryState;
};

/**
 * Return state for a subquery that's part of a 'join' or 'union' command. The subquery is itself a power query.
 *
 * @param tokens    list of subquery body tokens
 * @return query state
 */
const getSubqueryState = (tokens: Token[], query: string): FilterState => {
  let openingParenIndex = -1;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const prevToken = tokens[i - 1];
    if (
      tokens[i].type === TOKEN_TYPE.LEFT_PAREN &&
      (!prevToken || isAssignment(prevToken) || prevToken.type === TOKEN_TYPE.COMMA)
    ) {
      openingParenIndex = i;
      break;
    }
  }

  if (openingParenIndex >= 0) {
    const subquery = query.substring(tokens[openingParenIndex].end);
    // subquery cannot be a 'join' or 'union' command, so loop is not possible
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return isPowerQuery(subquery) ? getPowerQueryState(subquery) : getFilterState(subquery);
  }

  return EMPTY_FILTER_STATE;
};

/**
 * Return the state for a 'join' command, which takes the following form:
 *
 *   | join (QUERY), b = (QUERY) on COLUMN
 *
 * Each QUERY is a power query which can have its own commands. The only thing we really look for is
 * the change to complete the 'on' keyword.
 *
 * @param tokens          list of tokens representing the body of the command
 * @param query           text of the PQ
 * @param isInSubquery    if true, user is in middle of entering a parenthesized subquery
 *
 * @return filter state
 */

const getJoinCommandState = (tokens: Token[], query: string, isInSubquery: boolean): FilterState => {
  if (isInSubquery) {
    return getSubqueryState(tokens, query);
  }

  const last = tokens[tokens.length - 1] || ({} as Token);
  const queryState = cloneDeep(EMPTY_FILTER_STATE) as FilterState;
  queryState.text = last.text;
  const one = tokens[tokens.length - 2] || ({} as Token);
  const onFound = !!tokens.find((token) => token.type === TOKEN_TYPE.ON);
  const rightParens = tokens.filter((token) => token.type === TOKEN_TYPE.RIGHT_PAREN);
  if (
    rightParens.length >= 2 &&
    (last.type === TOKEN_TYPE.RIGHT_PAREN || (!last.complete && one.type === TOKEN_TYPE.RIGHT_PAREN))
  ) {
    queryState.states.push(FILTER_STATE.PQ_SUBQUERIES);
  } else if (onFound) {
    queryState.states.push(FILTER_STATE.PQ_JOIN_ON);
  }

  return queryState;
};

/**
 * Return the state for a 'union' command, which takes the following form:
 *
 *   | union (QUERY), b = (QUERY) | group ...
 *
 * Each QUERY is a power query which can have its own commands. After the queries, we can
 * just handle the current command.
 *
 * @param tokens          list of tokens representing the body of the command
 * @param query           text of the PQ
 * @param isInSubquery    if true, user is in middle of entering a parenthesized subquery
 *
 * @return filter state
 */
const getUnionCommandState = (tokens: Token[], query: string, isInSubquery: boolean): FilterState => {
  if (isInSubquery) {
    return getSubqueryState(tokens, query);
  }

  return EMPTY_FILTER_STATE;
};

/**
 * Given the tokens for a 'join' or 'union' command, find parenthesized subqueries and return their ranges.
 *
 * @param tokens          list of tokens representing the body of the command
 * @return list of subquery ranges with indexes of start and end tokens
 */
const getSubqueries = (tokens: Token[]): Subquery[] => {
  const subqueries = [] as Subquery[];
  const isCommandOrComma = (token: Token): boolean =>
    [TOKEN_TYPE.COMMAND, TOKEN_TYPE.COMMA].includes(token.type as TOKEN_TYPE);

  for (let i = 0; i < tokens.length; i += 1) {
    let token = tokens[i];
    const one = tokens[i - 1] || ({} as Token);
    const three = tokens[i - 3] || ({} as Token);
    if (
      token.type === TOKEN_TYPE.LEFT_PAREN &&
      (isCommandOrComma(one) || (isAssignment(one) && isCommandOrComma(three)))
    ) {
      const subquery = { start: i } as Subquery;
      let depth = 1;
      i += 1;
      while (depth > 0 && i < tokens.length) {
        token = tokens[i];
        if (token.type === TOKEN_TYPE.LEFT_PAREN) {
          depth += 1;
        } else if (token.type === TOKEN_TYPE.RIGHT_PAREN) {
          depth -= 1;
        }
        i += 1;
      }
      if (depth === 0) {
        subquery.end = i;
      }
      subqueries.push(subquery);
    }
  }

  return subqueries;
};

/**
 * Return a state object representing what can come next for the given power query.
 * Note: this is just a start; only handles up to command so far.
 *
 * @param query    a power query
 *
 * @return FilterState object with state, text that determined state, valid token types that can follow, field, and operator
 */
export const getPowerQueryState = (query: string): FilterState => {
  const tokens = tokenizePowerQuery(query);
  // console.log(tokens.map((t) => t.type).join(' '));
  const last = tokens[tokens.length - 1] || ({} as Token);
  const lastType = last?.type;
  let text = (!last?.complete && last.text) || '';

  let queryState = { field: '', op: '' } as FilterState;
  const states = [] as FILTER_STATE[];
  let addedTokens = [] as Token[];

  if (lastType === TOKEN_TYPE.PIPE) {
    // all we have is a pipe; set up to match all commands
    states.push(FILTER_STATE.PIPE);
    text = '';
  } else if (lastType === TOKEN_TYPE.COMMAND && (!last.complete || ['inner', 'left'].includes(last.text))) {
    // partial command - match against it
    states.push(FILTER_STATE.IN_COMMAND);
    if (last.complete) {
      text = last.text + ' ';
    }
  } else {
    // we have a command with at least one token following it
    // check to see if we're in a subquery of a 'join' or 'union' command; if so, ignore any command in a subquery
    let cmdTokenIndex = tokens.findIndex((token) => token.type === TOKEN_TYPE.COMMAND); // 'join' and 'union' must be first command
    let cmdToken = tokens[cmdTokenIndex] || ({} as Token);
    const isComplexCommand = COMPLEX_COMMANDS.includes(cmdToken.text);
    const subqueries = isComplexCommand ? getSubqueries(tokens) : ([] as Subquery[]);
    let isInSubquery = false;
    if (isComplexCommand) {
      // check for a command that comes after subqueries, which is possible with 'union'
      const lastSubquery = subqueries.length > 0 ? subqueries[subqueries.length - 1] : null;
      const lastSubqueryToken = lastSubquery && lastSubquery.end ? tokens[lastSubquery.end] : null;
      const lastSubqueryEnd = lastSubqueryToken?.end || 0;
      if (subqueries.length >= 2 && lastSubqueryEnd > 0) {
        const index = findIndexOfLastToken(
          tokens,
          (token) => token.type === TOKEN_TYPE.COMMAND && token.start > lastSubqueryEnd,
        );
        cmdTokenIndex = index !== -1 ? index : cmdTokenIndex;
      }
      // we're in a subquery if the last one has a start and no end
      isInSubquery = lastSubquery !== null && lastSubquery.start > 0 && !lastSubquery.end;
      for (let i = 0; i < subqueries.length; i += 1) {
        addedTokens = [
          ...addedTokens,
          ...findPowerQueryFields(tokens, subqueries[i].start, subqueries[i].end || tokens.length),
        ];
      }
    } else {
      cmdTokenIndex = findIndexOfLastToken(tokens, (token) => token.type === TOKEN_TYPE.COMMAND);
      addedTokens = findPowerQueryFields(tokens);
    }
    cmdToken = tokens[cmdTokenIndex];
    const body = tokens.slice(cmdTokenIndex + 1);
    const command = cmdToken?.text;

    switch (command) {
      case 'columns':
        queryState = getColumnsCommandState(body);
        break;
      case 'filter':
        queryState = getFilterCommandState(body, query);
        break;
      case 'group':
        queryState = getGroupCommandState(body);
        break;
      case 'let':
        queryState = getLetCommandState(body);
        break;
      case 'lookup':
        queryState = getLookupCommandState(body);
        break;
      case 'parse':
        queryState = getParseCommandState(body);
        break;
      case 'sort':
        queryState = getSimpleCommandState(body);
        break;
      case 'transpose':
        queryState = getSimpleCommandState(body);
        break;
      case 'inner join':
      case 'left join':
      case 'join':
        queryState = getJoinCommandState(body, query, isInSubquery);
        break;
      case 'union':
        queryState = getUnionCommandState(body, query, isInSubquery);
        break;
      default:
        queryState = EMPTY_FILTER_STATE;
    }
  }

  if (states.length > 0) {
    queryState.states = states;
  }
  queryState.text = text.trim();
  queryState.valid = uniq(
    queryState.states.reduce(
      (acc, state) => (FILTER_STATE_NEXT[state] ? acc.concat(FILTER_STATE_NEXT[state]) : acc),
      [] as TOKEN_TYPE[],
    ),
  );
  queryState.tokens = tokens;

  // gather PQ fields, excluding the one (if any) we're creating
  let curPqField: Token;
  const one = tokens[tokens.length - 2] || ({} as Token); // one token back
  if (isAssignment(one)) {
    curPqField = tokens[tokens.length - 3] || ({} as Token); // two tokens back
  }
  queryState.pqFields = [...tokens, ...addedTokens]
    .filter((token) => token.type === TOKEN_TYPE.PQ_FIELD && token !== curPqField)
    .map((token) => token.text);

  return queryState;
};

/**
 * Return autocomplete matches for the given type. Handles types that appear in power query commands
 * but not in regular search filters.
 *
 * @param type          type of token to match
 * @param queryState    object with state used for matching
 * @param query         the power query to complete
 *
 * @return list of matches, or null if the type is not handled here
 */
const getPowerQueryMatchesByType = (type: TOKEN_TYPE, queryState: FilterState, query: string): Match[] | null => {
  const matchText = queryState.text.toLowerCase(); // text used for matching
  const tokens = queryState.tokens || ([] as Token[]);
  const last = tokens[tokens.length - 1] || ({} as Token);

  if (!matchText && !COMPLETE_EMPTY_STRING[type]) {
    return [];
  }

  if (type === TOKEN_TYPE.COMMAND) {
    // insert a space between pipe and command if there isn't one
    const one = tokens[tokens.length - 2] || ({} as Token);
    const space = last.start === one.end ? ' ' : '';
    const allowAllCommands = tokens.length <= 2;
    return COMMANDS.filter(
      (command) => command.indexOf(matchText) === 0 && (allowAllCommands || !COMPLEX_COMMANDS.includes(command)),
    ).map((command) => ({ type, text: command, completionValue: space + command + ' ' }));
  }

  if (type === TOKEN_TYPE.PQ_FIELD) {
    return (queryState.pqFields || [])
      .filter((pqField) => pqField.toLowerCase().indexOf(matchText) === 0)
      .map((pqField) => ({ type, text: pqField, completionValue: pqField + ' ' }));
  }

  if (type === TOKEN_TYPE.BY) {
    if ('by'.indexOf(matchText.toLowerCase()) === 0) {
      // in 'lookup' command, 'by' after table name may need space prepended since table name is a string (being complete doesn't guarantee space after)
      const space = last.type === TOKEN_TYPE.STRING && last.end === query.length ? ' ' : '';
      return [{ type, text: 'by', completionValue: space + 'by ' }];
    }
  }

  if (type === TOKEN_TYPE.FROM) {
    if ('from'.indexOf(matchText.toLowerCase()) === 0) {
      return [{ type, text: 'from', completionValue: 'from ' }];
    }
  }

  if (type === TOKEN_TYPE.ON) {
    if ('on'.indexOf(matchText.toLowerCase()) === 0) {
      const space = last.type === TOKEN_TYPE.RIGHT_PAREN && last.end === query.length ? ' ' : '';
      return [{ type, text: 'on', completionValue: space + 'on ' }];
    }
  }

  if (type === TOKEN_TYPE.BOOLEAN) {
    return WORDS[type]
      .filter((value) => value.indexOf(matchText) === 0)
      .map((value) => ({ type: TOKEN_TYPE.BOOLEAN, text: value, completionValue: value + ' ' }));
  }

  if (PQ_FUNCTIONS[type].length > 0) {
    let matches = PQ_FUNCTIONS[type].filter((func) => func.indexOf(matchText) === 0);
    if (
      (matches.includes('avg') && matches.includes('average')) ||
      (matches.includes('overall_avg') && matches.includes('overall_average'))
    ) {
      matches = matches.filter((match) => match !== 'avg' && match !== 'overall_avg');
    }
    return matches.map((func) => ({
      type,
      text: func + '()',
      completionValue: func + (PQ_FUNCTIONS_NO_ARGS.includes(func) ? '()' : '('),
    }));
  }

  return null;
};

/**
 * Perform autocomplete matching at the end of the given power query.
 *
 * @param query        power query to complete
 * @param facetInfo    object with facet names and values
 * @param exclude      token types to exclude from matching
 *
 * @return MatchInfo with matches by type and matched text
 */
export const getPowerQueryMatches = (
  query: string,
  facetInfo?: FacetsCompletionInfo,
  exclude = [] as TOKEN_TYPE[],
): MatchInfo => {
  const queryState = getPowerQueryState(query);

  const result = {
    matchedText: queryState.text.replace(/^\|\s*/, ''),
    matches: {} as Record<TOKEN_TYPE, Match[]>,
  } as MatchInfo;

  let validTypes = queryState.valid || [];
  validTypes = validTypes.reduce((acc: TOKEN_TYPE[], type) => {
    const aggregateTypes = AGGREGATE_TOKEN_TYPE[type];
    return acc.concat(aggregateTypes.length > 0 ? aggregateTypes : [type]);
  }, []);

  validTypes
    .filter((type) => !exclude.includes(type))
    .forEach((type) => {
      result.matches[type] =
        getPowerQueryMatchesByType(type, queryState, query) || getFilterMatchesByType(type, queryState, facetInfo);
    });

  return result;
};

/**
 * Perform autocomplete matching at the end of the given filter or power query.
 *
 * @param query        power query to complete
 * @param facetInfo    object with facet names and values
 * @param exclude      token types to exclude from matching
 *
 * @return MatchInfo with matches by type and matched text
 */
export const getMatches = (
  query: string,
  facetInfo?: FacetsCompletionInfo,
  exclude = [] as TOKEN_TYPE[],
): MatchInfo => {
  if (isPowerQuery(query)) {
    return getPowerQueryMatches(query, facetInfo, exclude);
  }

  return getFilterMatches(query, facetInfo, exclude);
};
